import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core";
import { StyledTableRow } from "../../util/customFunctions";
import CopyTablesObjectv2 from "../../components/xlsx-export/CopyTablesObjectv2";

const useStyles = makeStyles(
  (theme) => ({
    root: {
      margin: "0.5rem",
      padding: "0.5rem",
    },
    sticky: {
      position: "sticky",
      left: -1,
      background: "white",
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        bottom: 0,
        right: 0,
        width: "0.5px",
        background: "lightgrey",
      },
    },
    colHeader: {
      fontSize: "0.85rem",
      border: "0.5px solid white",
      padding: "10px 15px",
      color: "white",
      fontWeight: 600,
    },
    dataCell: {
      fontSize: "0.85rem",
      border: "0.5px solid lightgrey",
      padding: "10px 15px",
      fontWeight: "inherit",
    },
    categoryLabel: {
      fontSize: "0.85rem",
      display: "inline-block",
      width: "100%",
      marginBottom: "2px",
      fontWeight: 600,
    },
    valueLabel: {
      fontSize: "0.85rem",
      fontWeight: 600,
    },
  }),
  { index: 1 }
);

// // Standard color palette for mix charts
// const COLOR_PALETTE = [
//   "#4472C4", // Blue
//   "#ED7D31", // Orange
//   "#A5A5A5", // Gray
//   "#FFC000", // Yellow
//   "#5B9BD5", // Light Blue
//   "#70AD47", // Green
//   "#264478", // Dark Blue
//   "#9E480E", // Dark Orange
//   "#636363", // Dark Gray
//   "#997300", // Dark Yellow
// ];

// // Light color variants for data cells
// const LIGHT_COLOR_MAP = {
//   "#4472C4": "#D6E3F8",
//   "#ED7D31": "#FCE4D6",
//   "#A5A5A5": "#EDEDED",
//   "#FFC000": "#FFF2CC",
//   "#5B9BD5": "#DEEAF6",
//   "#70AD47": "#E2EFD9",
//   "#264478": "#D6DFEC",
//   "#9E480E": "#F4DDCC",
//   "#636363": "#E7E7E7",
//   "#997300": "#FFF4CC",
// };


const COLOR_PALETTE = [
  "#FFF59D", // Light Yellow
  "#91CBF8", // Light Blue
  "#81DFEB", // Teal
  "#9FA8DA", // Light Indigo
  "#FFAB91", // Deep Orange
  "#CE93D8", // Light Purple
  "#DCE775", // Lime
  "#BCAAA4", // Light Brown
  "#EF9B9A", // Light Red
];

// Cell background colors — each maps to itself
const LIGHT_COLOR_MAP = {
  "#FFF59D": "#FFF59D",
  "#91CBF8": "#91CBF8",
  "#81DFEB": "#81DFEB",
  "#9FA8DA": "#9FA8DA",
  "#FFAB91": "#FFAB91",
  "#CE93D8": "#CE93D8",
  "#DCE775": "#DCE775",
  "#BCAAA4": "#BCAAA4",
  "#EF9B9A": "#EF9B9A",
};

// Parse the value from string like "Others ($56,498,762)" or "Healthcare & Hospitals (3.8%)"
const parseValue = (str) => {
  if (!str) return { label: "-", value: "-" };
  
  const match = str.match(/^(.+?)\s*\((.+?)\)$/);
  if (match) {
    const label = match[1].trim();
    const valueStr = match[2].trim();
    
    // Check if it's a dollar amount
    if (valueStr.startsWith("$")) {
      const numValue = parseFloat(valueStr.replace(/[$,]/g, ""));
      const formattedValue = formatCurrency(numValue);
      return { label, value: formattedValue, rawValue: numValue };
    }
    
    // Otherwise it's a percentage or other value
    return { label, value: valueStr, rawValue: valueStr };
  }
  
  return { label: str, value: str, rawValue: str };
};

// Format currency to K or M
const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "-";
  }

  const absNum = Math.abs(value);
  
  if (absNum > 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (absNum > 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  
  return `$${value.toLocaleString()}`;
};

// Build a map of primary dimensions (labels) to colors
const buildDimensionColorMap = (data, columns) => {
  const dimensionSet = new Set();
  
  // Extract all unique primary dimensions from the data
  data.forEach(row => {
    columns.forEach(col => {
      if (col.field !== 'rank') {
        const cellValue = row[col.field];
        const { label } = parseValue(cellValue);
        if (label && label !== "-") {
          dimensionSet.add(label);
        }
      }
    });
  });
  
  // Assign colors to each unique dimension
  const dimensionColorMap = {};
  const dimensions = Array.from(dimensionSet).sort(); // Sort for consistency
  dimensions.forEach((dimension, index) => {
    dimensionColorMap[dimension] = COLOR_PALETTE[index % COLOR_PALETTE.length];
  });
  
  return dimensionColorMap;
};

function DataTableWIthColorCoding({ columns, data, dataTest, title }) {
  const classes = useStyles();
  const tableRef = useRef();
  const tableContainerRef = useRef();

  const [isSticky, setIsSticky] = useState(false);
  const [rowPin, setrowPin] = useState(false);
  const [parentWidth, setparentWidth] = useState();

  // Build the dimension to color mapping
  const dimensionColorMap = buildDimensionColorMap(data, columns);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;

    const handleScroll = () => {
      if (tableContainer?.scrollLeft > 0) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
      if (tableContainer?.scrollTop > 0) {
        setrowPin(true);
      } else {
        setrowPin(false);
      }
    };

    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (tableContainerRef.current) {
      const width = tableContainerRef.current.getBoundingClientRect().width;
      setparentWidth(width);
    }
  }, []);

  // Get the color for a cell based on its primary dimension (label)
  const getCellColor = (cellValue, isHeader = false) => {
    const { label } = parseValue(cellValue);
    const baseColor = dimensionColorMap[label] || COLOR_PALETTE[0];
    
    if (isHeader) {
      return baseColor;
    }
    
    return LIGHT_COLOR_MAP[baseColor] || "#F0F0F0";
  };

  return (
    <div style={{ maxWidth: "100%", padding: "2rem", paddingTop: 0, paddingBottom: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.5rem' }}>
        <Typography align="left" variant="body2" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <div>
          {parentWidth !== undefined && <CopyTablesObjectv2 tableRef={tableRef} />}
        </div>
      </div>
      <TableContainer
        style={{
          width: "auto",
          borderLeft: isSticky ? "0.5px solid lightgrey" : "",
          borderTop: rowPin ? "0.5px solid lightgrey" : "",
          maxHeight: "85vh",
          paddingBottom: "1rem",
        }}
        id="myTableContainer-Second"
        ref={tableContainerRef}
      >
        <Table ref={tableRef} data-test={dataTest}>
          <TableHead>
            <TableRow>
              {columns.map((col, idx) => {
                // For rank column, use default blue
                if (col.field === "rank") {
                  return (
                    <TableCell
                      key={idx}
                      align="center"
                      className={classes.colHeader}
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 6,
                         backgroundColor: "#4471c4",
                        color: "white",
                        width: "80px",
                      }}
                    >
                      {col.label}
                    </TableCell>
                  );
                }
                
                // For other columns, determine color based on the most common dimension
                // We'll look at the first row to determine the header color
                const firstRowValue = data[0]?.[col.field];
                const headerColor = firstRowValue ? getCellColor(firstRowValue, true) : COLOR_PALETTE[idx % COLOR_PALETTE.length];
                
                return (
                  <TableCell
                    key={idx}
                    align="center"
                    className={classes.colHeader}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 6,
                     backgroundColor: "#4471c4",
                      color: "white",
                      width: "120px",
                    }}
                  >
                    {col.label}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, rowIdx) => (
              <StyledTableRow key={rowIdx}>
                {columns.map((col, colIdx) => {
                  const cellValue = row[col.field];
                  
                  // For rank column, just display as is
                  if (col.field === "rank") {
                    return (
                      <TableCell
                        key={colIdx}
                        className={classes.dataCell}
                        align="center"
                        style={{
                        //   backgroundColor: LIGHT_COLOR_MAP[COLOR_PALETTE[0]],
                          fontWeight: 600,
                        }}
                      >
                        {cellValue}
                      </TableCell>
                    );
                  }
                  
                  // For other columns, parse and display label + value
                  const { label, value } = parseValue(cellValue);
                  const bgColor = getCellColor(cellValue, false);
                  
                  return (
                    <TableCell
                      key={colIdx}
                      className={classes.dataCell}
                      align="center"
                      style={{
                        backgroundColor: bgColor,
                      }}
                    >
                      <span className={classes.categoryLabel}>{label}</span>
                      {" "}
                      <span className={classes.valueLabel}>{value}</span>
                    </TableCell>
                  );
                })}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default DataTableWIthColorCoding;